/**
 * Create an outreach message to a candidate — `POST /outreach/messages`, WP-W11.
 * `web/shapes/outreach.md` §4.
 *
 * Targets a candidate BY ID, never a free-text address — the picker pattern
 * `CreateApplicationForm`/`OffersScreen` already use: a `<select>` labelled by name
 * when the candidates list is reachable (`recruiting.view_candidates`), a plain id
 * input otherwise. `subject` is optional and **omitted, not sent empty**, because
 * `exactOptionalPropertyTypes` makes those two different requests — the same
 * discipline `CreateOfferForm` keeps for its own optional fields.
 *
 * There is no "send" affordance here or anywhere on this screen: the message opens
 * `pending_approval` and the worker decides what happens to it next
 * (`web/shapes/outreach.md` §1.3).
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  OutreachChannel,
  OutreachMessage,
  OutreachMessageCreateRequest,
} from '../../api/outreach';
import { OUTREACH_CHANNELS } from '../../api/outreach';
import { OUTREACH_CHANNEL_LABEL } from '../../recruiting/outreach';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';
import type { PickerOption } from './CreateApplicationForm';

export interface CreateOutreachMessageFormProps {
  /** `null` when the candidates list is out of reach — the field falls back to an id input. */
  readonly candidateOptions: readonly PickerOption[] | null;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly created: OutreachMessage | null;
  readonly onSubmit: (body: OutreachMessageCreateRequest) => void;
}

export function CreateOutreachMessageForm({
  candidateOptions,
  pending,
  error,
  created,
  onSubmit,
}: CreateOutreachMessageFormProps): ReactNode {
  const { t } = useI18n();
  const [candidateId, setCandidateId] = useState('');
  const [channel, setChannel] = useState<OutreachChannel>('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const ready = candidateId.trim() !== '' && body.trim() !== '';

  return (
    <section className="wp-section" aria-labelledby="w11-create-heading" data-testid="w11-create">
      <h2 className="wp-section__title" id="w11-create-heading">
        {t('outreach.messages.create.title')}
      </h2>
      <p className="wp-small wp-muted">{t('outreach.messages.create.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || pending) return;
          const trimmedSubject = subject.trim();
          onSubmit({
            candidate_id: candidateId.trim(),
            channel,
            body: body.trim(),
            ...(trimmedSubject === '' ? {} : { subject: trimmedSubject }),
          });
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w11-create-candidate">
            {t('outreach.messages.create.candidate')}
          </label>
          {candidateOptions === null ? (
            <>
              <input
                className="wp-field__input"
                id="w11-create-candidate"
                data-testid="w11-create-candidate"
                type="text"
                value={candidateId}
                autoComplete="off"
                onChange={(event) => {
                  setCandidateId(event.target.value);
                }}
              />
              <p className="wp-small wp-muted">{t('outreach.messages.create.candidateIdHint')}</p>
            </>
          ) : (
            <select
              className="wp-field__input"
              id="w11-create-candidate"
              data-testid="w11-create-candidate"
              value={candidateId}
              onChange={(event) => {
                setCandidateId(event.target.value);
              }}
            >
              <option value="">{t('outreach.messages.create.candidatePlaceholder')}</option>
              {candidateOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w11-create-channel">
            {t('outreach.messages.create.channel')}
          </label>
          <select
            className="wp-field__input"
            id="w11-create-channel"
            data-testid="w11-create-channel"
            value={channel}
            onChange={(event) => {
              setChannel(event.target.value as OutreachChannel);
            }}
          >
            {OUTREACH_CHANNELS.map((value) => (
              <option key={value} value={value}>
                {t(OUTREACH_CHANNEL_LABEL[value])}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w11-create-subject">
            {t('outreach.messages.create.subject')}
          </label>
          <input
            className="wp-field__input"
            id="w11-create-subject"
            data-testid="w11-create-subject"
            type="text"
            value={subject}
            maxLength={500}
            autoComplete="off"
            onChange={(event) => {
              setSubject(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w11-create-body">
            {t('outreach.messages.create.body')}
          </label>
          <textarea
            className="wp-field__input"
            id="w11-create-body"
            data-testid="w11-create-body"
            value={body}
            maxLength={50000}
            rows={4}
            onChange={(event) => {
              setBody(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w11-create-submit"
            disabled={!ready || pending}
          >
            {pending
              ? t('outreach.messages.create.submitting')
              : t('outreach.messages.create.submit')}
          </button>
        </div>
      </form>

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('outreach.messages.subjectWord')}
          testId="w11-create-error"
        />
      )}
      {created === null ? null : (
        <StatusNote
          severity="low"
          title={t('outreach.messages.create.ok')}
          testId="w11-create-ok"
        />
      )}
    </section>
  );
}
